import { AlertTriangle, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useMaintenanceStatus } from 'src/hooks/useMaintenanceStatus';
import { ContainerBox, FlexContainer, IconButton, TypographyText } from 'src/tef/components';

const formatDateTime = (iso: string) => {
  try {
    return new Date(iso).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/New_York',
      timeZoneName: 'short',
    });
  } catch {
    return iso;
  }
};

const MaintenanceBanner: React.FC = () => {
  const { window, isUpcoming } = useMaintenanceStatus();
  const [dismissedId, setDismissedId] = useState<string | null>(null);

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('maintenance_banner_dismissed_id');
      if (saved) setDismissedId(saved);
    } catch {
      setDismissedId(null);
    }
  }, []);

  if (!isUpcoming || !window) return null;
  if (dismissedId === window.id) return null;

  const handleDismiss = () => {
    try {
      sessionStorage.setItem('maintenance_banner_dismissed_id', window.id);
    } catch {
      return;
    }
    setDismissedId(window.id);
  };

  const fallback = `Scheduled maintenance from ${formatDateTime(window.scheduled_start)} to ${formatDateTime(window.scheduled_end)}.`;
  const customMessage = window.notice_message?.trim();
  const messageText = customMessage || fallback;

  return (
    <ContainerBox className="relative border-b border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 flex-shrink-0">
      <FlexContainer align="center" justify="center" gap="sm" className="w-full px-12 py-2.5 text-center">
        <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
        <ContainerBox className="min-w-0">
          <TypographyText variant={{ size: 'sm' }} className="text-amber-900 dark:text-amber-100">
            <TypographyText as="span" className="font-semibold">
              Scheduled maintenance:
            </TypographyText>{' '}
            {customMessage ? (
              <>{customMessage}</>
            ) : (
              <>
                Tropel will be unavailable from {formatDateTime(window.scheduled_start)} to{' '}
                {formatDateTime(window.scheduled_end)}.
              </>
            )}
          </TypographyText>
          {customMessage && (
            <TypographyText variant={{ size: 'xs' }} className="text-amber-700 dark:text-amber-300 mt-0.5">
              {fallback}
            </TypographyText>
          )}
        </ContainerBox>
      </FlexContainer>
      <IconButton
        icon={<X className="w-4 h-4" />}
        variant={{ style: 'ghost', size: 'sm' }}
        onClick={handleDismiss}
        aria-label="Dismiss notice"
        className="absolute right-2 top-1/2 -translate-y-1/2 text-amber-700 dark:text-amber-300"
      />
      <TypographyText className="sr-only">{messageText}</TypographyText>
    </ContainerBox>
  );
};

export default MaintenanceBanner;
